import { Component } from '../../../components/Component.js';
import * as dom from '../../../core/utils/dom.js';
import { eventBus, EVENTS } from '../../../core/events/index.js';
import { CharacterCardComponent } from './CharacterCardComponent.js';
import { CharacterModalComponent } from './CharacterModalComponent.js';
import { FilterComponent } from './FilterComponent.js';
import { FAQComponent } from './FAQComponent.js';
import { RecentChatsView } from './RecentChatsView.js';
import { CDN_URL } from '../../../js/config.js';
import { storageManager } from '../../../core/storage/index.js';
import { TemplateLoader } from './templates/templateLoader.js';
import { getAnalyticsTracker } from '../../../js/analytics/hangout-obs-tracker.js';
import { applyTranslations, getCurrentLanguage, interpolate } from '../../../js/translations.js';
import { getCurrentUserId } from '../../../js/utils/auth_utils.js';
import { getGenderPreference, getStylePreference, getNsfwEnabled } from '../../../js/utils/userPrefs.js';
import { buildNewConversationConfig } from './hangout-config-builder.js';
import { isPlaceholderCharacterName, sanitizeCharacterName, pickResolvedCharacterName } from './character-name-utils.js';
import { resolveScenarioRecordDisplay } from '../services/scenarioMappers.js';
import { isTrialOfferEligible } from '../../../js/utils/pricingPlans.js';
import {
  ensureBlockedContentLoaded,
  isCharacterBlocked,
  isScenarioBlocked,
  refreshBlockedContent,
  subscribeBlockedContent
} from '../../../js/moderation/blockedContentStore.js';
import { isGoogleOneTapAuthPending } from '../../../js/utils/googleOneTap.js';

const BANNER_CHAT_CHARACTER_ID = '71';
const BANNER_CHAT_SCENARIO_ID = 31;
const BANNER_CHAT_SFW_SCENARIO_ID = 115;
const BANNER_DRAG_SLOP_PX = 10;
const BANNER_DRAG_COMMIT_PX = 48;
const BANNER_DRAG_COMMIT_RATIO = 0.15;
const BANNER_DRAG_RUBBER = 0.35;
const BANNER_TRACK_TRANSITION = 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)';

export class RankingsComponent extends Component {
  /**
   * @param {HTMLElement} element
   * @param {RankingsComponentOptions} options
   */
  constructor(element, options = {}) {
    super(element, options);

    this.promotionalScenarios = [];

    this.analyticsTracker = getAnalyticsTracker();
    this.infiniteScrollObserver = null;
    this.handleCharactersUpdated = this.renderCharacters.bind(this);
    this.handleRankingsError = this.handleRankingsError.bind(this);
    this.lastCharactersLoad = 'initial';
    this.firstRenderPromise ??= null;
    this.resolveFirstRender ??= null;
    this.firstRenderResolved ??= false;
    this.deferredHydrationPromise ??= null;
    this.templatePreloadPromise ??= null;
  }

  async init() {
    this.ensureFirstRenderPromise();
    this.handleBannerSlideClick = this.handleBannerSlideClick.bind(this);
    this.handleBannerSlideKeyDown = this.handleBannerSlideKeyDown.bind(this);

    const opts = this.options || {};
    this.rankingsState = opts['state'];
    this.service = opts['service'];

    this.characterCards = new Map();
    this.characterModal = null;
    this.filterComponent = null;
    this.faqComponent = null;
    this.recentChatsView = null;
    this.activeRankingsView = 'characters';
    this.handleRankingsViewTabClick = this.handleRankingsViewTabClick.bind(this);

    this.handleLanguageChange = this.handleLanguageChange.bind(this);
    this.handleAuthLoginSuccess = this.handleAuthLoginSuccess.bind(this);
    this.handleAuthLogout = this.handleAuthLogout.bind(this);
    this.handleBlockedContentChanged = this.handleBlockedContentChanged.bind(this);
    this.handlePremiumMembershipLearnMoreClick = this.handlePremiumMembershipLearnMoreClick.bind(this);
    document.addEventListener('languageChanged', this.handleLanguageChange);
    document.addEventListener('auth:login:success', this.handleAuthLoginSuccess);
    document.addEventListener('auth:logout', this.handleAuthLogout);
    this.unsubscribeBlockedContent = subscribeBlockedContent(this.handleBlockedContentChanged);

    this.createContainers();

    try {
      this.templatePreloadPromise = TemplateLoader.preloadTemplates().catch(error => {
        console.warn('Failed to preload templates:', error);
      });

      await this.loadInitialData();
      this.initComponents();
      this.registerEventListeners();

      this.state = { ...this.state, isInitialized: true };
      console.info('[Rankings][boot]', {
        entrypoint: 'rankings',
        characterCount: this.rankingsState?.characters?.length ?? 0,
        branch: this.lastCharactersLoad
      });
    } catch (error) {
      this.resolveFirstRenderReady(false);
      console.error('Failed to initialize rankings:', error);
      if (this.element) {
        this.element.innerHTML = `
          <div class="error-message">
            <h2>Failed to initialize rankings</h2>
            <p>${error.message}</p>
            <button onclick="window.location.reload()">Retry</button>
          </div>
        `;
      }
    }
  }

  ensureFirstRenderPromise() {
    if (this.firstRenderPromise) return;
    this.firstRenderPromise = new Promise(resolve => {
      this.resolveFirstRender = resolve;
    });
  }

  resolveFirstRenderReady(isReady) {
    this.ensureFirstRenderPromise();
    if (this.firstRenderResolved) return;
    this.firstRenderResolved = true;
    this.resolveFirstRender?.(isReady);
    this.resolveFirstRender = null;
  }

  waitForFirstRender() {
    this.ensureFirstRenderPromise();
    return this.firstRenderPromise;
  }

  async hydrateDeferredRankingsData() {
    if (this.deferredHydrationPromise) return this.deferredHydrationPromise;

    this.deferredHydrationPromise = (async () => {
      const currentLang = getCurrentLanguage() || 'en';
      const deferredTasks = [
        this.templatePreloadPromise,
        this.service.preloadDeferredMappings(currentLang),
        this.loadDeferredScenarios(),
        this.loadUserFavorites(),
        this.loadUserPrivateCharacters()
      ].filter(Boolean);

      await Promise.allSettled(deferredTasks);
      await this.loadPromotionalScenarios();
    })().catch(error => {
      console.warn('[RankingsComponent] Deferred hydration failed:', error);
    });

    return this.deferredHydrationPromise;
  }

  async loadDeferredScenarios() {
    try {
      await this.service.getScenarios();
    } catch (error) {
      console.warn('[RankingsComponent] Failed to load deferred scenarios:', error);
    }
  }

  handleLanguageChange(e) {
    if (e.detail?.language) {
      try {
        const currentLang = getCurrentLanguage();
        if (currentLang && typeof currentLang === 'string') {
          applyTranslations('rankings', currentLang);
        }
        if (this.lastPremiumCharacterCount != null) {
          this.updatePremiumMembershipNote(this.lastPremiumCharacterCount);
        }
      } catch (error) {
        console.error('Error applying translations:', error);
      }
    }
  }

  async handleAuthLoginSuccess() {
    try {
      await refreshBlockedContent();
      await this.loadUserFavorites();
      await this.loadUserPrivateCharacters();
      this.service?.clearCharacterListCache?.();
      await this.reloadCharactersAfterBlockChange();
    } catch (error) {
      console.warn('[RankingsComponent] Failed to refresh auth-scoped data after login:', error);
    }
  }

  handleAuthLogout() {
    this.rankingsState?.setFavoriteCharacterIds?.([]);
    this.rankingsState?.setPrivateCharacterIds?.([]);
    this.service?.clearCharacterListCache?.();
    void this.reloadCharactersAfterBlockChange();
  }

  getActiveCharacterFilters() {
    const filters = this.rankingsState?.filters || {};
    return {
      nameFilter: filters.nameFilter || '',
      private: filters.private || false,
      favorite: filters.favorite || false,
      male: filters.male ?? getGenderPreference(),
      style: filters.style || getStylePreference(),
      nsfw: filters.nsfw ?? getNsfwEnabled()
    };
  }

  async reloadCharactersAfterBlockChange() {
    if (!this.rankingsState) return;
    this.lastRenderedKey = '';
    this.lastRenderedIdsArray = [];
    await this.loadCharacters({
      page: 1,
      filters: this.getActiveCharacterFilters(),
      source: 'blocked',
      render: true
    });
  }

  handleBlockedContentChanged(detail) {
    if (!detail || detail.action === 'load') return;
    if (detail.action === 'block' && detail.kind === 'character') {
      this.removeCharacterCardsByIds([detail.id]);
      return;
    }
    if (detail.action === 'block') return;
    this.service?.clearCharacterListCache?.();
    void this.reloadCharactersAfterBlockChange();
  }

  async applyDeferredBlockedFilter() {
    const shown = this.rankingsState?.characters || [];
    const blockedIds = shown
      .filter((character) => isCharacterBlocked(character.id))
      .map((character) => character.id);
    if (!blockedIds.length) return;
    this.removeCharacterCardsByIds(blockedIds);
  }

  createContainers() {
    this.element.innerHTML = `
      <div id="banner-carousel-section" class="banner-carousel-container">
        <div class="banner-carousel-wrapper">
          <div class="banner-carousel" id="banner-carousel">
            ${this.createBannerSlides()}
          </div>
          <button class="carousel-nav carousel-prev" id="carousel-prev" aria-label="Previous banner">
            <i class="fas fa-chevron-left"></i>
          </button>
          <button class="carousel-nav carousel-next" id="carousel-next" aria-label="Next banner">
            <i class="fas fa-chevron-right"></i>
          </button>
          <div class="carousel-indicators" id="carousel-indicators">
            ${this.getBannerSlides().map((_, i) => `<button class="indicator ${i === 0 ? 'active' : ''}" data-slide="${i}"></button>`).join('')}
          </div>
        </div>
      </div>
      <div class="rankings-sticky-chrome">
        <nav id="rankings-view-tabs" class="rankings-view-tabs" aria-label="Rankings views">
          <div class="rankings-view-tabs__list" role="tablist">
            <button type="button" role="tab" id="rankings-tab-characters" class="rankings-view-tab is-active" aria-selected="true" aria-controls="rankings-container" data-view="characters" data-translate="rankings.viewCharacters"><span>Characters</span></button>
            <button type="button" role="tab" id="rankings-tab-recent" class="rankings-view-tab" aria-selected="false" aria-controls="recent-chats-container" data-view="recent" data-translate="rankings.viewRecentChats"><span>Recent chats</span></button>
            <!-- TODO: show Creators when per-user profile pages exist -->
            <button type="button" role="tab" id="rankings-tab-creators" class="rankings-view-tab" hidden aria-selected="false" data-view="creators" data-translate="rankings.viewCreators"><span>Creators</span></button>
          </div>
        </nav>
        <div id="side-buttons"></div>
      </div>
      <div class="grid-container" id="rankings-container"></div>
      <div class="grid-container recent-chats-container" id="recent-chats-container" hidden></div>
      <div id="infinite-scroll-trigger" class="infinite-scroll-trigger"></div>
      <div id="premium-membership-note" class="premium-membership-note" hidden>
        <p class="premium-membership-note__summary">
          <span class="premium-membership-note__text"></span>
          (<button type="button" class="premium-membership-note__learn-more" aria-expanded="false"></button>)
        </p>
        <p class="premium-membership-note__details" hidden></p>
      </div>
      <div id="faq"></div>
    `;

    this.rankingsViewTabs = this.element.querySelector('#rankings-view-tabs');
    this.rankingsContainer = this.element.querySelector('#rankings-container');
    this.recentChatsContainer = this.element.querySelector('#recent-chats-container');
    this.sideButtonsContainer = this.element.querySelector('#side-buttons');
    this.infiniteScrollTrigger = this.element.querySelector('#infinite-scroll-trigger');
    this.rankingsViewTabs?.addEventListener('click', this.handleRankingsViewTabClick);
    this.premiumMembershipNote = this.element.querySelector('#premium-membership-note');
    this.faqContainer = this.element.querySelector('#faq');
    this.premiumMembershipDetailsOpen = false;
    this.premiumMembershipNote?.querySelector('.premium-membership-note__learn-more')
      ?.addEventListener('click', this.handlePremiumMembershipLearnMoreClick);
    this.element.addEventListener('click', this.handleBannerSlideClick);
    this.element.addEventListener('keydown', this.handleBannerSlideKeyDown);

    try {
      const currentLang = getCurrentLanguage();
      if (currentLang && typeof currentLang === 'string') {
        applyTranslations('rankings', currentLang);
      }
    } catch (error) {
      console.error('Error applying translations:', error);
    }
  }

  getBannerSlides() {
    const slides = [
      {
        titleKey: 'rankings.banner1Title',
        subtitleKey: 'rankings.banner1Subtitle',
        title: 'An Image on Every Message',
        subtitle: 'No cookies, no ads. We don\'t sell your data. Chat privately with the best AI companions.',
        index: 0
      },
      {
        titleKey: 'rankings.banner2Title',
        subtitleKey: 'rankings.banner2Subtitle',
        title: 'Create new images',
        subtitle: 'Copy images from other characters, or design your own!',
        index: 1
      },
      {
        titleKey: 'rankings.banner3Title',
        subtitleKey: 'rankings.banner3Subtitle',
        title: 'Explore New Features',
        subtitle: 'Discover what is new in the app',
        index: 2
      },
      {
        titleKey: 'rankings.banner4Title',
        subtitleKey: 'rankings.banner4Subtitle',
        title: 'Tired of the same old stories?',
        subtitle: 'Use our AI to create your own Scenarios, Characters and Locations',
        index: 3
      },
    ];

    if (this.shouldShowPremiumPassCta()) {
      return slides;
    }

    return slides.filter(slide => slide.index !== 2);
  }

  createBannerSlides() {
    const slides = this.getBannerSlides();
    const isDesktop = window.matchMedia('(min-width: 1025px)').matches;

    return slides.map((slide, i) => {
      const fileName = `slide${slide.index + 1}${isDesktop ? '_desktop' : ''}.webp`;
      const bannerSrc = `${CDN_URL}/data/header-banner/${fileName}`;
      const eagerSrc = i === 0 ? `src="${bannerSrc}"` : '';
      return `
      <div class="banner-slide ${i === 0 ? 'active' : ''}" data-slide-index="${slide.index}">
        <img
          ${eagerSrc}
          data-src="${bannerSrc}"
          alt=""
          class="banner-image"
          draggable="false"
        >
        <div role="button" tabindex="0" class="banner-slide-hit" data-slide-index="${slide.index}" aria-label="${slide.title}" data-translate-attr="aria-label:${slide.titleKey}"></div>
        <div class="banner-overlay">
          <h3 data-translate="${slide.titleKey}">${slide.title}</h3>
          <p data-translate="${slide.subtitleKey}">${slide.subtitle}</p>
        </div>
      </div>
    `;
    }).join('');
  }

  handleBannerSlideKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.banner-slide-hit')) return;
    event.preventDefault();
    this.handleBannerSlideClick(event);
  }

  handleBannerSlideClick(event) {
    if (this.suppressBannerClick || isGoogleOneTapAuthPending()) return;
    const target = event.target instanceof Element ? event.target : null;
    const hit = target?.closest('.banner-slide-hit');
    if (!hit || !this.element.contains(hit)) return;

    const slideIndex = hit.getAttribute('data-slide-index');
    if (slideIndex === '0') {
      void this.handleChatWithHer(event, slideIndex);
      return;
    }
    if (slideIndex === '1') {
      window.router?.navigate('/create-video') || (window.location.href = '/create-video.html');
      return;
    }
    if (slideIndex === '2') {
      window.router?.navigate('/premium-pass.html') || (window.location.href = '/premium-pass.html');
      return;
    }
    if (slideIndex === '3') {
      window.router?.navigate('/create-scenario') || (window.location.href = '/create-scenario.html');
    }
  }

  handleRankingsViewTabClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const tab = target?.closest('.rankings-view-tab');
    if (!tab || tab.hidden) return;
    const view = tab.getAttribute('data-view');
    if (view === 'characters' || view === 'recent') {
      this.setRankingsView(view);
    }
  }

  scrollToStickyChrome() {
    const banner = this.element?.querySelector('#banner-carousel-section');
    const chrome = this.element?.querySelector('.rankings-sticky-chrome');
    if (banner) {
      const marginBottom = Number.parseFloat(getComputedStyle(banner).marginBottom) || 0;
      const y = window.scrollY + banner.getBoundingClientRect().bottom + marginBottom;
      window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
      return;
    }
    if (chrome) {
      window.scrollTo({ top: Math.max(0, chrome.offsetTop), behavior: 'auto' });
    }
  }

  setRankingsView(view) {
    const nextView = view === 'recent' ? 'recent' : 'characters';
    const didSwitch = this.activeRankingsView !== nextView;
    this.activeRankingsView = nextView;
    const isCharacters = nextView === 'characters';

    this.rankingsViewTabs?.querySelectorAll('.rankings-view-tab').forEach((tab) => {
      const isActive = tab.getAttribute('data-view') === nextView;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    if (this.sideButtonsContainer) this.sideButtonsContainer.hidden = !isCharacters;
    if (this.rankingsContainer) this.rankingsContainer.hidden = !isCharacters;
    if (this.recentChatsContainer) this.recentChatsContainer.hidden = isCharacters;

    if (isCharacters) {
      this.syncInfiniteScrollTrigger();
      this.updatePremiumMembershipNote(this.lastPremiumCharacterCount);
      if (didSwitch) this.scrollToStickyChrome();
      return;
    }

    if (this.premiumMembershipNote) this.premiumMembershipNote.hidden = true;
    this.syncInfiniteScrollTrigger();
    void this.recentChatsView?.load();
    if (didSwitch) this.scrollToStickyChrome();
  }

  syncInfiniteScrollTrigger() {
    if (!this.infiniteScrollTrigger) return;
    if (this.activeRankingsView === 'characters') {
      this.infiniteScrollTrigger.hidden = false;
      return;
    }
    this.infiniteScrollTrigger.hidden = !this.recentChatsView?.hasMore;
  }

  initComponents() {
    try {
      this.filterComponent = new FilterComponent(this.sideButtonsContainer, {
        state: this.rankingsState,
        onFilterChange: this.handleFilterChange.bind(this)
      });
    } catch (error) {
      console.error('Failed to initialize FilterComponent:', error);
    }

    try {
      const modalElement = dom.$('#characterModal') || this.createModalElement();
      this.characterModal = new CharacterModalComponent(modalElement, {
        state: this.rankingsState,
        service: this.service,
        onPickAdditionalModeChange: this.handlePickAdditionalModeChange.bind(this)
      });
    } catch (error) {
      console.error('Failed to initialize CharacterModalComponent:', error);
    }

    try {
      this.faqComponent = new FAQComponent(this.faqContainer);
    } catch (error) {
      console.error('Failed to initialize FAQComponent:', error);
    }

    try {
      this.recentChatsView = new RecentChatsView(this.recentChatsContainer, {
        state: this.rankingsState,
        service: this.service,
        onHasMoreChange: () => this.syncInfiniteScrollTrigger()
      });
      this.recentChatsView.start();
    } catch (error) {
      console.error('Failed to initialize RecentChatsView:', error);
    }

    try {
      this.initCarousel();
    } catch (error) {
      console.error('Failed to initialize banner carousel:', error);
    }
  }

  createModalElement() {
    const modalElement = document.createElement('div');
    modalElement.id = 'characterModal';
    modalElement.className = 'modal';
    document.body.appendChild(modalElement);
    return modalElement;
  }

  initCarousel() {
    this.currentSlide = 0; // Zero-based index, no clones
    this.isTransitioning = false;
    /** @type {HTMLElement} */
    this.carousel = /** @type {HTMLElement} */ (this.element.querySelector('#banner-carousel'));
    this.slides = Array.from(this.element.querySelectorAll('.banner-slide'));
    this.indicators = this.element.querySelectorAll('.indicator');
    this.prevButton = this.element.querySelector('#carousel-prev');
    this.nextButton = this.element.querySelector('#carousel-next');
    /** @type {HTMLElement} */
    this.carouselWrapper = /** @type {HTMLElement} */ (this.element.querySelector('.banner-carousel-wrapper'));
    this.loadedVideos = new Set([0]); // First banner image is loaded by default
    this.loadingVideos = new Set(); // Banner images currently being loaded

    if (!this.carousel || !this.slides.length) {
      console.warn('Carousel elements not found');
      return;
    }

    // No clones: finite carousel sized to the rendered slides
    this.slideCount = this.slides.length;

    // Dynamically set widths
    const slideWidthPercent = 100 / this.slides.length;
    this.carousel.style.width = `${this.slides.length * 100}%`;
    this.slides.forEach(s => {
      s.style.flex = `0 0 ${slideWidthPercent}%`;
      s.style.maxWidth = `${slideWidthPercent}%`;
      s.style.width = `${slideWidthPercent}%`;
    });

    // Data attributes for later use
    this.slides.forEach((slide, index) => {
      /** @type {HTMLImageElement} */
      const img = /** @type {HTMLImageElement} */ (slide.querySelector('img'));
      if (img) {
        slide.dataset.originalSrc = img.src;
      }
      slide.dataset.slideIndexAll = index.toString(); // includes clones
    });

    // Event listeners
    this.prevButton?.addEventListener('click', () => this.prevSlideManual());
    this.nextButton?.addEventListener('click', () => this.nextSlideManual());
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });

    this.handleBannerPointerDown = this.handleBannerPointerDown.bind(this);
    this.handleBannerPointerMove = this.handleBannerPointerMove.bind(this);
    this.handleBannerPointerUp = this.handleBannerPointerUp.bind(this);
    this.handleBannerPointerCancel = this.handleBannerPointerCancel.bind(this);
    this.handleBannerTouchMove = this.handleBannerTouchMove.bind(this);
    this.carouselWrapper.addEventListener('pointerdown', this.handleBannerPointerDown);
    this.carouselWrapper.addEventListener('touchmove', this.handleBannerTouchMove, { passive: false });

    // Jump to first real slide position without transition
    this.carousel.style.transition = 'none';
    this.updateCarousel();
    // Force a reflow to ensure the position is set before enabling transitions
    this.carousel.offsetHeight;
    requestAnimationFrame(() => {
      this.carousel.style.transition = 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
    });

  }

  // No transition end handler needed for finite carousel

  nextSlideManual() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.currentSlide = (this.currentSlide + 1) % this.slideCount;
    this.updateCarousel();
    setTimeout(() => { this.isTransitioning = false; }, 650);
  }

  prevSlideManual() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.currentSlide = (this.currentSlide - 1 + this.slideCount) % this.slideCount;
    this.updateCarousel();
    setTimeout(() => { this.isTransitioning = false; }, 650);
  }

  goToSlide(realIndex) {
    if (this.isTransitioning || realIndex < 0 || realIndex >= this.slideCount) return;
    this.isTransitioning = true;
    this.currentSlide = realIndex;
    this.updateCarousel();
    setTimeout(() => { this.isTransitioning = false; }, 650);
  }

  handleBannerPointerDown(event) {
    if (event.button !== 0) return;
    if (this.isTransitioning || this.bannerDrag) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.carousel-nav, .indicator')) return;
    if (!this.carousel || !this.carouselWrapper) return;

    const width = this.carouselWrapper.getBoundingClientRect().width;
    this.bannerDrag = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      originX: -this.currentSlide * width,
      lastDx: 0,
      dragging: false
    };
    this.startBannerPointerTracking();
  }

  startBannerPointerTracking() {
    window.addEventListener('pointermove', this.handleBannerPointerMove, { passive: false });
    window.addEventListener('pointerup', this.handleBannerPointerUp);
    window.addEventListener('pointercancel', this.handleBannerPointerCancel);
  }

  stopBannerPointerTracking() {
    this.bannerDrag = null;
    if (!this.handleBannerPointerMove) return;
    window.removeEventListener('pointermove', this.handleBannerPointerMove);
    window.removeEventListener('pointerup', this.handleBannerPointerUp);
    window.removeEventListener('pointercancel', this.handleBannerPointerCancel);
  }

  handleBannerPointerMove(event) {
    const drag = this.bannerDrag;
    if (!drag || event.pointerId !== drag.pointerId || !this.carousel || !this.carouselWrapper) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    drag.lastDx = dx;
    if (!drag.dragging) {
      if (Math.abs(dx) < BANNER_DRAG_SLOP_PX && Math.abs(dy) < BANNER_DRAG_SLOP_PX) return;
      if (Math.abs(dx) <= Math.abs(dy)) {
        this.stopBannerPointerTracking();
        return;
      }
      drag.dragging = true;
      this.carouselWrapper.classList.add('is-dragging');
    }

    event.preventDefault();
    this.carousel.style.transition = 'none';
    this.carousel.style.transform = `translateX(${drag.originX + this.getBannerDragDelta(dx)}px)`;
  }

  handleBannerTouchMove(event) {
    if (!this.bannerDrag?.dragging) return;
    event.preventDefault();
  }

  getBannerDragDelta(dx) {
    const atStart = this.currentSlide === 0;
    const atEnd = this.currentSlide >= this.slideCount - 1;
    if ((atStart && dx > 0) || (atEnd && dx < 0)) return dx * BANNER_DRAG_RUBBER;
    return dx;
  }

  handleBannerPointerUp(event) {
    const drag = this.bannerDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.finishBannerDrag(event.clientX - drag.startX, drag.dragging, false);
  }

  handleBannerPointerCancel(event) {
    const drag = this.bannerDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (drag.dragging) {
      this.finishBannerDrag(drag.lastDx, true, false);
      return;
    }
    this.finishBannerDrag(0, false, true);
  }

  finishBannerDrag(dx, wasDragging, cancelled) {
    this.stopBannerPointerTracking();
    this.carouselWrapper?.classList.remove('is-dragging');
    if (!wasDragging) return;

    this.suppressBannerClick = true;
    setTimeout(() => { this.suppressBannerClick = false; }, 0);
    if (this.carousel) this.carousel.style.transition = BANNER_TRACK_TRANSITION;

    if (!cancelled) {
      const width = this.carouselWrapper?.getBoundingClientRect().width || 0;
      const commit = Math.max(BANNER_DRAG_COMMIT_PX, width * BANNER_DRAG_COMMIT_RATIO);
      if (dx <= -commit) {
        this.nextSlideManual();
        return;
      }
      if (dx >= commit) {
        this.prevSlideManual();
        return;
      }
    }

    this.updateCarousel();
  }

  updateCarousel() {
    if (!this.slides || !this.indicators) return;

    // Translate
    if (this.carousel && this.carouselWrapper) {
      const containerWidth = this.carouselWrapper.getBoundingClientRect().width;
      const translateX = -this.currentSlide * containerWidth;
      this.carousel.style.transform = `translateX(${translateX}px)`;
    }

    // Update active slide opacity - only real slides should be active
    this.slides.forEach((slide, idx) => {
      const isCurrent = idx === this.currentSlide;
      slide.classList.toggle('active', isCurrent);
    });

    // Update indicators: 1:1 with slides
    this.indicators.forEach((indicator, idx) => indicator.classList.toggle('active', idx === this.currentSlide));

    // Buttons are never disabled in looping mode
    if (this.prevButton) this.prevButton.disabled = false;
    if (this.nextButton) this.nextButton.disabled = false;

    this.loadVideoForSlide(this.currentSlide);

    this.preloadNextVideo();
  }

  loadVideoForSlide(slideIndex) {
    // Prevent duplicate loading requests
    if (this.loadedVideos.has(slideIndex) ||
      this.loadingVideos.has(slideIndex) ||
      !this.slides[slideIndex]) {
      return;
    }

    const slide = this.slides[slideIndex];
    /** @type {HTMLImageElement | null} */
    const image = slide.querySelector('img.banner-image');
    const dataSrc = image?.getAttribute('data-src');

    if (image && dataSrc && !image.getAttribute('src')) {
      this.loadingVideos.add(slideIndex);

      const handleLoadSuccess = () => {
        this.loadedVideos.add(slideIndex);
        this.loadingVideos.delete(slideIndex);
        image.removeEventListener('load', handleLoadSuccess);
        image.removeEventListener('error', handleLoadError);
      };

      const handleLoadError = (error) => {
        this.loadingVideos.delete(slideIndex);
        image.removeEventListener('load', handleLoadSuccess);
        image.removeEventListener('error', handleLoadError);
        console.error(`Failed to load banner image ${slideIndex}:`, error);
      };

      image.addEventListener('load', handleLoadSuccess);
      image.addEventListener('error', handleLoadError);
      image.src = dataSrc;
    }
  }

  preloadNextVideo() {
    if (this.preloadTimeout) {
      clearTimeout(this.preloadTimeout);
    }

    const nextSlide = (this.currentSlide + 1) % this.slideCount;

    // Don't preload if already loaded or currently loading
    if (!this.loadedVideos.has(nextSlide) && !this.loadingVideos.has(nextSlide)) {
      this.preloadTimeout = setTimeout(() => {
        // Double-check state hasn't changed during timeout
        const stillCurrentSlide = (this.currentSlide + 1) % this.slideCount === nextSlide;
        const stillNotLoaded = !this.loadedVideos.has(nextSlide) && !this.loadingVideos.has(nextSlide);

        if (stillCurrentSlide && stillNotLoaded) {
          this.loadVideoForSlide(nextSlide);
        }
      }, 800);
    }
  }

  registerEventListeners() {
    eventBus.on(EVENTS.RANKINGS.CHARACTERS_UPDATED, this.handleCharactersUpdated);
    eventBus.on(EVENTS.RANKINGS.ERROR_CHANGED, this.handleRankingsError);

    this.registerAnalyticsEventListeners();

    this.setupInfiniteScroll();
  }

  registerAnalyticsEventListeners() {
    const trackFilter = () => this.analyticsTracker.trackFeature('rankings_filter_used');

    const privateFilter = document.getElementById('private-filter');
    privateFilter?.addEventListener('click', trackFilter);

    const favoriteFilter = document.getElementById('favorite-filter');
    favoriteFilter?.addEventListener('click', trackFilter);

    document.addEventListener('genderToggleClicked', () => {
      this.analyticsTracker.trackFeature('gender_toggled');
    });

    const nameFilter = document.getElementById('name-filter');
    nameFilter?.addEventListener('input', () => {
      if (!nameFilter.value) return;
      trackFilter();
    });
  }

  setupInfiniteScroll() {
    if (!this.infiniteScrollTrigger) {
      console.warn('Infinite scroll trigger element not found');
      return;
    }

    const handle = dom.observeWhenVisible(
      this.infiniteScrollTrigger,
      () => {
        if (this.activeRankingsView === 'recent') {
          void this.recentChatsView?.loadMore();
          return;
        }
        void this.loadMoreCharacters();
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
    this.infiniteScrollObserver = handle;
  }

  async loadCharacters({ page, offset, filters, source, append = false, render = false }) {
    this.lastCharactersLoad = source;
    const response = await this.service.getCharacters({
      page,
      offset,
      limit: this.rankingsState.itemsPerPage,
      filters
    });
    this.rankingsState.setError(null);
    const metadata = {
      totalPages: response.totalPages || 1,
      totalCharacters: response.totalCharacters || 0
    };
    if (append) this.rankingsState.addCharacters(response.data || [], metadata);
    else this.rankingsState.setCharacters(response.data || [], metadata);
    if (render) this.renderCharacters({ characters: append ? this.rankingsState.characters : (response.data || []) });
    return response;
  }

  async loadMoreCharacters() {
    if (!this.rankingsState || this.rankingsState.isLoading || !this.rankingsState.hasMoreCharacters) {
      return;
    }

    try {
      this.rankingsState.setLoading(true);
      const offset = this.rankingsState.characters.length;
      const perPage = this.rankingsState.itemsPerPage || 15;
      const nextPage = Math.floor(offset / perPage) + 1;

      this.analyticsTracker?.trackFeature('catalog_load_more');
      this.rankingsState.setCurrentPage(nextPage);

      await this.loadCharacters({
        page: nextPage,
        offset,
        filters: this.getActiveCharacterFilters(),
        source: 'more',
        append: true
      });
    } catch (error) {
      console.error('Failed to load more characters:', error);
      this.rankingsState.setError(error);
    } finally {
      this.rankingsState.setLoading(false);
    }
  }

  async loadInitialData() {
    if (!this.rankingsState) {
      console.error('rankingsState is undefined');
      return;
    }

    try {
      this.rankingsState.setLoading(true);
      const blockedReady = ensureBlockedContentLoaded();
      const filters = {
        nameFilter: '',
        private: false,
        male: getGenderPreference(),
        style: getStylePreference(),
        favorite: false,
        nsfw: getNsfwEnabled()
      };
      this.rankingsState.setFilters(filters);
      const charactersResponse = await this.loadCharacters({
        page: 1,
        filters,
        source: 'initial',
        render: true
      });

      this.lastPremiumCharacterCount = charactersResponse.premiumCharacterCount || 0;
      this.updatePremiumMembershipNote(this.lastPremiumCharacterCount);
      this.resolveFirstRenderReady(true);
      void blockedReady
        .then(() => this.applyDeferredBlockedFilter())
        .catch((error) => {
          console.warn('[RankingsComponent] Deferred blocked filter failed:', error);
        });

    } catch (error) {
      this.resolveFirstRenderReady(false);
      console.error('Failed to load initial data:', error);
      this.rankingsState.setError(error);
    } finally {
      this.rankingsState.setLoading(false);
    }
  }

  async loadUserFavorites() {
    try {
      const userId = getCurrentUserId();
      if (!userId) {
        this.rankingsState.setFavoriteCharacterIds([]);
        return;
      }

      const favoriteData = await this.service.getUserFavoriteCharacters(userId);
      const favoriteIds = favoriteData.favorites || [];

      this.rankingsState.setFavoriteCharacterIds(favoriteIds);

      // Update existing character cards with favorite status
      this.updateCharacterCardFavorites();
    } catch (error) {
      console.error('Failed to load user favorites:', error);
      // On error, set empty favorites
      this.rankingsState.setFavoriteCharacterIds([]);
    }
  }

  async loadUserPrivateCharacters() {
    try {
      const userId = getCurrentUserId();
      if (!userId) {
        this.rankingsState.setPrivateCharacterIds([]);
        return;
      }

      const privateCharacters = await this.service.getUserPrivateCharacters(userId);
      const privateIds = privateCharacters.map(c => c.id);

      this.rankingsState.setPrivateCharacterIds(privateIds);

    } catch (error) {
      console.error('Failed to load user private characters:', error);
      // On error, set empty private characters
      this.rankingsState.setPrivateCharacterIds([]);
    }
  }

  getScenarioCharacterOutfitId(scenarioCharacter = null) {
    const rawOutfitId = scenarioCharacter?.outfit_id ?? scenarioCharacter?.outfit;
    if (rawOutfitId === undefined || rawOutfitId === null || rawOutfitId === '') return null;
    return String(rawOutfitId);
  }

  normalizeOutfitId(outfitId, fallbackOutfitId = '0') {
    if (outfitId === undefined || outfitId === null || outfitId === '') return String(fallbackOutfitId);
    return String(outfitId);
  }

  getBannerChatScenarioId() {
    return getNsfwEnabled() ? BANNER_CHAT_SCENARIO_ID : BANNER_CHAT_SFW_SCENARIO_ID;
  }

  async loadPromotionalScenarios() {
    const slideToScenarioMapping = { 0: this.getBannerChatScenarioId(), 1: 30, 2: 32, 3: 5 };

    try {
      const { data: scenarios } = await this.service.getScenarios();
      if (!Array.isArray(scenarios) || scenarios.length === 0) {
        this.promotionalScenarios = [];
        return;
      }

      const characterPromises = Object.entries(slideToScenarioMapping).map(async ([slideIndex, scenarioId]) => {
        const normalizedScenarioId = String(scenarioId);
        const scenario = scenarios.find(s => String(s.id) === normalizedScenarioId);
        if (!scenario || isScenarioBlocked(normalizedScenarioId)) return null;

        const primaryCharacter = scenario.chars?.[0];
        const characterId = primaryCharacter?.id;
        const normalizedCharacterId = characterId !== undefined && characterId !== null
          ? String(characterId)
          : null;
        if (isCharacterBlocked(normalizedCharacterId)) return null;

        let characterName = normalizedCharacterId
          ? sanitizeCharacterName(`Character ${normalizedCharacterId}`)
          : 'Character';

        if (normalizedCharacterId) {
          try {
            const stateChar = this.rankingsState?.characters?.find(c =>
              String(c.id) === normalizedCharacterId
            );

            if (stateChar?.name && !isPlaceholderCharacterName(stateChar.name, normalizedCharacterId)) {
              characterName = sanitizeCharacterName(stateChar.name);
            } else {
              const characterDetails = await this.service.getCharacterDetails(characterId);
              if (characterDetails?.name && !isPlaceholderCharacterName(characterDetails.name, normalizedCharacterId)) {
                characterName = sanitizeCharacterName(characterDetails.name);
              }
            }
          } catch (error) {
            console.warn(`Failed to get character name for ID ${normalizedCharacterId}:`, error);
          }
        }

        const resolvedScenario = resolveScenarioRecordDisplay(scenario, [
          this.rankingsState?.characters,
          { id: characterId, name: characterName }
        ]);
        return {
          slide: parseInt(slideIndex),
          id: resolvedScenario.id,
          scenarioId: resolvedScenario.id,
          characterId: characterId || null,
          characterName: characterName,
          name: resolvedScenario.name,
          description: resolvedScenario.description || '',
          goal: scenario.goal,
          loc_id: resolvedScenario.loc_id,
          family: primaryCharacter?.family,
          outfit: this.getScenarioCharacterOutfitId(primaryCharacter),
          startTime: scenario.startTime,
          tags: resolvedScenario.tags || [],
          chars: resolvedScenario.chars || []
        };
      });

      this.promotionalScenarios = (await Promise.all(characterPromises)).filter(Boolean);
    } catch (error) {
      console.error('Failed to load promotional scenarios:', error);
      this.promotionalScenarios = [];
    }
  }

  removeCreateCharacterCard() {
    if (!this.rankingsContainer) return;
    const existing = this.rankingsContainer.querySelector('.create-character-card');
    if (existing) existing.remove();
  }

  appendCreateCharacterCard() {
    if (!this.rankingsContainer) return;

    // Ensure only one exists and it always sits at the end of the grid.
    this.removeCreateCharacterCard();

    const cardElement = document.createElement('div');
    cardElement.className = 'character-card rest create-character-card';
    cardElement.id = 'create-character-card';
    cardElement.setAttribute('role', 'button');
    cardElement.setAttribute('tabindex', '0');
    cardElement.setAttribute('aria-label', 'Create a character');

    cardElement.innerHTML = `
      <div class="card-inner">
        <div class="card-front create-character-front">
          <div class="create-character-plus" aria-hidden="true">+</div>
          <div class="create-character-title" data-translate="rankings.createCharacter">Create Character</div>
        </div>
      </div>
    `;

    const handleActivate = () => {
      window.location.href = './create-character.html';
    };

    cardElement.addEventListener('click', handleActivate);
    cardElement.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      handleActivate();
    });

    this.rankingsContainer.appendChild(cardElement);
  }

  handlePremiumMembershipLearnMoreClick(event) {
    event?.preventDefault?.();
    this.premiumMembershipDetailsOpen = !this.premiumMembershipDetailsOpen;
    const detailsEl = this.premiumMembershipNote?.querySelector('.premium-membership-note__details');
    const learnMoreBtn = this.premiumMembershipNote?.querySelector('.premium-membership-note__learn-more');
    if (detailsEl) detailsEl.hidden = !this.premiumMembershipDetailsOpen;
    if (learnMoreBtn) learnMoreBtn.setAttribute('aria-expanded', String(this.premiumMembershipDetailsOpen));
  }

  updatePremiumMembershipNote(count) {
    const noteEl = this.premiumMembershipNote;
    if (!noteEl) return;

    const premiumCount = Number(count) || 0;
    const textEl = noteEl.querySelector('.premium-membership-note__text');
    const learnMoreBtn = noteEl.querySelector('.premium-membership-note__learn-more');
    const detailsEl = noteEl.querySelector('.premium-membership-note__details');

    if (premiumCount <= 0) {
      noteEl.hidden = true;
      this.premiumMembershipDetailsOpen = false;
      if (textEl) textEl.textContent = '';
      if (learnMoreBtn) {
        learnMoreBtn.textContent = '';
        learnMoreBtn.setAttribute('aria-expanded', 'false');
      }
      if (detailsEl) {
        detailsEl.hidden = true;
        detailsEl.textContent = '';
      }
      return;
    }

    const lang = getCurrentLanguage();
    const t = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
    const summaryTemplate = t['rankings.premiumMembershipNote']
      || '{count} characters require a paid account';
    const learnMoreLabel = t['rankings.premiumMembershipLearnMore'] || 'learn more';
    const detailsText = t['rankings.premiumMembershipDetails']
      || "Characters including the tags 'loli', 'shota', 'aged down' are locked behind paid accounts";

    if (textEl) textEl.textContent = interpolate(summaryTemplate, { count: premiumCount });
    if (learnMoreBtn) {
      learnMoreBtn.textContent = learnMoreLabel;
      learnMoreBtn.setAttribute('aria-expanded', String(!!this.premiumMembershipDetailsOpen));
      learnMoreBtn.setAttribute('aria-label', learnMoreLabel);
    }
    if (detailsEl) {
      detailsEl.textContent = detailsText;
      detailsEl.hidden = !this.premiumMembershipDetailsOpen;
    }
    noteEl.hidden = this.activeRankingsView !== 'characters';
  }

  renderCharacters({ characters }) {
    // Prevent redundant renders of the same data
    // Use unsorted IDs to ensure order changes trigger re-render (important for ordered rankings)
    const charactersKey = characters?.map(c => c.id).join(',') || '';
    if (this.lastRenderedKey === charactersKey && charactersKey) {
      this.appendCreateCharacterCard();
      return;
    }
    // QoL: Append-only render on load-more (avoid re-rendering previous cards/images)
    const prevIds = this.lastRenderedIdsArray || [];
    const currIds = Array.isArray(characters) ? characters.map(c => c.id) : [];
    const canAppend = prevIds.length > 0
      && currIds.length > prevIds.length
      && prevIds.every((id, idx) => id === currIds[idx]);

    if (canAppend) {
      this.removeCreateCharacterCard();
      for (let index = prevIds.length; index < characters.length; index++) {
        this.appendCharacterCard(characters[index], index);
      }

      this.updateCharacterCardFavorites();

      this.appendCreateCharacterCard();
      this.lastRenderedKey = charactersKey;
      this.lastRenderedIdsArray = currIds;
      return;
    }

    this.lastRenderedKey = charactersKey;
    this.lastRenderedIdsArray = currIds;

    if (!Array.isArray(characters)) {
      console.error('characters is not an array');
      return;
    }

    this.characterCards.forEach(card => card.destroy());
    this.characterCards.clear();
    this.rankingsContainer.innerHTML = '';

    if (characters.length === 0) {
      this.showNoResultsMessage();
      this.removeCreateCharacterCard();
      return;
    }

    characters.forEach((character, index) => {
      this.appendCharacterCard(character, index);
    });

    this.updateCharacterCardFavorites();
    this.appendCreateCharacterCard();
  }

  removeCharacterCardsByIds(ids) {
    const idSet = new Set((ids || []).map((id) => String(id)).filter(Boolean));
    if (!idSet.size) return;

    this.service?.clearCharacterListCache?.();

    for (const [key, card] of [...this.characterCards.entries()]) {
      if (!idSet.has(String(key))) continue;
      card.destroy();
      card.element?.remove();
      this.characterCards.delete(key);
    }

    this.rankingsState?.removeCharactersByIds?.(Array.from(idSet));

    const remainingIds = (this.rankingsState?.characters || []).map((character) => character.id);
    this.lastRenderedIdsArray = remainingIds;
    this.lastRenderedKey = remainingIds.join(',');

    if (!remainingIds.length && this.rankingsContainer) {
      if (this.rankingsState?.hasMoreCharacters) {
        void this.loadMoreCharacters();
        return;
      }
      this.showNoResultsMessage();
      this.removeCreateCharacterCard();
    }
  }

  appendCharacterCard(character, index) {
    if (!character?.id) return;

    const cardElement = document.createElement('div');
    cardElement.className = index === 0 ? 'character-card top1' :
      index === 1 ? 'character-card top2' :
        index === 2 ? 'character-card top3' : 'character-card rest';
    this.rankingsContainer.appendChild(cardElement);

    try {
      const card = new CharacterCardComponent(cardElement, {
        character,
        rankingsState: this.rankingsState,
        isPrivate: !!this.rankingsState?.filters?.private,
        onCardClick: this.handleCardClick.bind(this)
      });
      this.characterCards.set(character.id, card);
    } catch (error) {
      console.error('Failed to create CharacterCardComponent:', error);
      cardElement.remove();
    }
  }


  handleRankingsError({ error } = {}) {
    if (!error) return;
    if (this.lastCharactersLoad === 'more') return;
    this.renderListError();
  }

  renderListError() {
    if (!this.rankingsContainer) return;

    this.characterCards.forEach(card => card.destroy());
    this.characterCards.clear();
    this.rankingsContainer.innerHTML = '';

    const errorElement = document.createElement('div');
    errorElement.className = 'rankings-list-error';
    errorElement.innerHTML = `
      <div class="no-results-content">
        <h3>Couldn't load characters</h3>
        <p>Something went wrong while loading the rankings list.</p>
        <button class="retry-btn" type="button">Retry</button>
      </div>
    `;

    const retryBtn = errorElement.querySelector('.retry-btn');
    retryBtn?.addEventListener('click', () => {
      if (this.lastCharactersLoad === 'filter') {
        this.handleFilterChange(this.rankingsState?.filters || {});
        return;
      }
      this.loadInitialData();
    });

    this.rankingsContainer.appendChild(errorElement);
  }

  showNoResultsMessage() {
    const noResultsElement = document.createElement('div');
    noResultsElement.className = 'no-results-message';
    noResultsElement.innerHTML = `
      <div class="no-results-content">
        <h3>No characters found</h3>
        <p>Your current filters don't match any characters.</p>
        <p>Try adjusting your filters or clearing them to see more characters.</p>
        <button class="clear-filters-btn">Clear All Filters</button>
      </div>
    `;

    // Bind the clear filters method to the button
    const clearBtn = noResultsElement.querySelector('.clear-filters-btn');
    if (clearBtn) clearBtn.addEventListener('click', () => this.clearAllFilters());

    this.rankingsContainer.appendChild(noResultsElement);
    this.removeCreateCharacterCard();
  }

  clearAllFilters() {
    if (this.filterComponent && this.filterComponent.resetFilters) {
      // Use the filter component's resetFilters method to properly reset without breaking event listeners
      this.filterComponent.resetFilters();
    }
  }

  /**
   * Apply side-panel filters to bring a target character into the visible rankings set.
   * @param {{ name?: string, private?: boolean, gender?: string|number, male?: boolean }} character
   */
  async applyCharacterNavigationFilters(character = {}) {
    if (!this.filterComponent || typeof this.filterComponent.applyCharacterNavigationFilters !== 'function') {
      return;
    }

    const genderValue = character.gender;
    const hasExplicitMale = typeof character.male === 'boolean';
    const isMale = hasExplicitMale
      ? character.male
      : genderValue === 1 || genderValue === '1' || String(genderValue || '').toLowerCase() === 'male';

    this.filterComponent.applyCharacterNavigationFilters({
      name: pickResolvedCharacterName(character, character.id) || '',
      isPrivate: !!character.private,
      isMale
    });
  }

  handleCardClick(character) {
    const characterId = typeof character === 'object' ? character.id : character;
    const characterData = typeof character === 'object'
      ? character
      : this.rankingsState?.characters?.find(c => c.id === characterId);

    if (!characterId) {
      console.warn('No valid character ID provided');
      return;
    }

    if (this.characterModal?.isPickAdditionalMode?.()) {
      const chosenIds = this.characterModal.getPartyCharacterIds?.() || [];
      if (chosenIds.some(id => String(id) === String(characterId))) {
        return;
      }
      this.characterModal.addPartyCharacter(characterId).catch(console.error);
      return;
    }

    this.rankingsState?.setSelectedCharacterId(characterId);

    eventBus.emit(EVENTS.RANKINGS.CHARACTER_SELECTED, {
      selectedCharacterId: characterId,
      character: characterData || { id: characterId, name: 'Unknown', rank: '?' }
    });
  }

  handlePickAdditionalModeChange(payload = {}) {
    const active = !!payload.active;
    const partyCharacterIds = payload.partyCharacterIds || [];
    if (active) {
      this.showPickAdditionalCharacterHint();
      this.setPartyPickDisabledCards(partyCharacterIds);
      return;
    }
    this.clearPickAdditionalCharacterHint();
    this.setPartyPickDisabledCards([]);
  }

  showPickAdditionalCharacterHint() {
    this.clearPickAdditionalCharacterHint();
    const sideButtons = this.sideButtonsContainer || this.element?.querySelector('#side-buttons');
    if (!sideButtons) return;

    const hint = document.createElement('p');
    hint.id = 'pick-additional-character-hint';
    hint.className = 'pick-additional-character-hint';
    hint.textContent = 'Select an additional character to join your scenario';
    sideButtons.insertAdjacentElement('afterend', hint);
  }

  clearPickAdditionalCharacterHint() {
    this.element?.querySelector('#pick-additional-character-hint')?.remove();
    document.getElementById('pick-additional-character-hint')?.remove();
  }

  setPartyPickDisabledCards(partyCharacterIds = []) {
    const disabledIds = new Set((partyCharacterIds || []).map(id => String(id)));
    const cards = this.rankingsContainer?.querySelectorAll('.character-card[data-id]') || [];
    cards.forEach((card) => {
      if (card.classList.contains('create-character-card')) return;
      const id = card.getAttribute('data-id');
      const shouldDisable = disabledIds.has(String(id));
      card.classList.toggle('party-pick-disabled', shouldDisable);
      if (shouldDisable) {
        card.setAttribute('aria-disabled', 'true');
      } else {
        card.removeAttribute('aria-disabled');
      }
    });
  }

  async handleFilterChange(filters, { skipScroll = false } = {}) {
    if (!this.rankingsState) {
      console.error('rankingsState is undefined');
      return;
    }

    if (this.filterChangeTimeout) {
      clearTimeout(this.filterChangeTimeout);
    }

    this.filterChangeTimeout = setTimeout(async () => {
      try {
        if (!skipScroll) this.scrollToStickyChrome();
        this.rankingsState.setLoading(true);
        this.rankingsState.setFilters(filters);
        this.rankingsState.setCurrentPage(1);
        await this.loadCharacters({ page: 1, filters, source: 'filter', render: true });
      } catch (error) {
        console.error('Error during filter change:', error);
        this.rankingsState.setError(error);
      } finally {
        this.rankingsState.setLoading(false);
      }
    }, 150);
  }

  promotionalHangoutExtras(scenario, extras = {}) {
    const locId = scenario.loc_id || scenario.loc;
    return {
      goal: scenario.goal,
      type: 'scenario',
      customScenarioDescription: scenario.description || '',
      backgroundUrl: locId ? `${CDN_URL}/data/backgrounds_images/${locId}.1.webp` : undefined,
      isAuthenticated: !!getCurrentUserId(),
      startTime: scenario.startTime,
      tags: scenario.tags || [],
      ...extras
    };
  }

  async resolveBannerCharacterName(characterId) {
    const normalizedCharacterId = String(characterId);
    const stateChar = this.rankingsState?.characters?.find((character) => String(character.id) === normalizedCharacterId);
    if (stateChar?.name && !isPlaceholderCharacterName(stateChar.name, normalizedCharacterId)) {
      return sanitizeCharacterName(stateChar.name);
    }

    try {
      const characterDetails = await this.service?.getCharacterDetails?.(characterId);
      if (characterDetails?.name && !isPlaceholderCharacterName(characterDetails.name, normalizedCharacterId)) {
        return sanitizeCharacterName(characterDetails.name);
      }
    } catch (error) {
      console.warn(`Failed to get character name for ID ${normalizedCharacterId}:`, error);
    }

    return sanitizeCharacterName(`Character ${normalizedCharacterId}`);
  }

  async handleChatWithHer(event, slideIndex) {
    event.preventDefault();

    const slide = parseInt(slideIndex, 10);
    let promotionalScenario = this.promotionalScenarios?.find(s => s.slide === slide);
    const wantedBannerScenarioId = slide === 0 ? String(this.getBannerChatScenarioId()) : null;
    const cachedBannerScenarioId = promotionalScenario ? String(promotionalScenario.scenarioId) : '';
    const bannerScenarioIsStale = Boolean(wantedBannerScenarioId) && cachedBannerScenarioId !== wantedBannerScenarioId;

    if (!promotionalScenario || bannerScenarioIsStale) {
      try {
        await this.loadPromotionalScenarios();
        promotionalScenario = this.promotionalScenarios?.find(s => s.slide === slide);
      } catch (error) {
        console.error('Failed to reload promotional scenarios:', error);
      }

      if (!promotionalScenario) {
        window.location.href = '/hangout.html';
        return;
      }
    }

    let { scenarioId, characterId, characterName } = promotionalScenario;
    const scenario = promotionalScenario;

    if (parseInt(slideIndex, 10) === 0 && String(characterId) !== BANNER_CHAT_CHARACTER_ID) {
      characterId = BANNER_CHAT_CHARACTER_ID;
      characterName = await this.resolveBannerCharacterName(BANNER_CHAT_CHARACTER_ID);
    }

    try {
      let character = this.rankingsState?.characters?.find(c => c.id == characterId);
      const scenarioCharacter = (scenario.chars || []).find((entry) => String(entry?.id) === String(characterId)) || null;

      if (!character && scenarioCharacter) {
        const scenarioOutfitId = this.getScenarioCharacterOutfitId(scenarioCharacter);

        character = {
          id: characterId,
          name: characterName || `Character ${characterId}`,
          outfitId: this.normalizeOutfitId(scenarioOutfitId, '0')
        };
      } else if (!character) {
        character = {
          id: characterId,
          name: characterName || `Character ${characterId}`
        };
      }

      if (character && characterName && (!character.name || isPlaceholderCharacterName(character.name, characterId))) {
        character = { ...character, name: characterName };
      }

      if (window.HangoutState?.getInstance) {
        const hangoutState = window.HangoutState.getInstance();
        await hangoutState.setSelectedCharacter(character);

        const currentScenarios = hangoutState.getScenarios() || [];
        const existingIndex = currentScenarios.findIndex(s => s.id === scenario.id);
        const updatedScenarios = [...currentScenarios];

        if (existingIndex >= 0) {
          updatedScenarios[existingIndex] = scenario;
        } else {
          updatedScenarios.push(scenario);
        }

        await hangoutState.setScenarios(updatedScenarios);
        await hangoutState.setSelectedScenario(scenario);

        if (scenario.loc_id || scenario.loc) {
          const loc_id = scenario.loc_id || scenario.loc;
          const backgroundUrl = `${CDN_URL}/data/backgrounds_images/${loc_id}.1.webp`;
          await hangoutState.setSelectedBackground(backgroundUrl);
        }

        await hangoutState.addCharacter(character);
      }

      const scenarioOutfitId = this.getScenarioCharacterOutfitId(scenarioCharacter);
      const outfitId = this.normalizeOutfitId(scenarioOutfitId ?? character.outfitId, '0');
      const characterWithOutfit = { ...character, outfitId };
      const hangoutConfig = {
        ...buildNewConversationConfig({
          scenarioId,
          character: characterWithOutfit,
          carouselCharacters: [{
            id: characterId,
            name: character.name || characterName || `Character ${characterId}`,
            outfitId
          }],
          selectedOutfitId: outfitId,
          isPrivateScenario: false,
          loc_id: scenario.loc_id || scenario.loc,
          selectedScenario: scenario,
          entrypoint: 'rankings_promotional'
        }),
        ...this.promotionalHangoutExtras(scenario, {
          character: characterWithOutfit,
          selectedCharacterId: characterId,
          isPromotionalScenario: true
        })
      };

      await storageManager.setItem('currentHangoutConfig', hangoutConfig);
      window.location.href = './hangout.html';
    } catch (error) {
      console.error('Error setting up hangout configuration:', error);
      window.location.href = '/hangout.html';
    }
  }

  updateCharacterCardFavorites() {
    // Update all existing character cards with current favorite status
    // This is mainly used when favorites are initially loaded
    if (!this.rankingsState?.favoriteCharacterIds) {
      return;
    }

    this.characterCards.forEach((card, characterId) => {
      if (card && card.updateFavoriteDisplay) {
        const isFavorited = this.rankingsState.isCharacterFavorited(characterId);
        card.updateFavoriteDisplay(isFavorited);
      }
    });
  }

  shouldShowPremiumPassCta() {
    try {
      return isTrialOfferEligible();
    } catch (error) {
      console.error('[RankingsComponent] Error reading premium pass CTA eligibility:', error);
      return true;
    }
  }

  destroy() {
    document.removeEventListener('languageChanged', this.handleLanguageChange);
    document.removeEventListener('auth:login:success', this.handleAuthLoginSuccess);
    document.removeEventListener('auth:logout', this.handleAuthLogout);
    this.unsubscribeBlockedContent?.();
    this.unsubscribeBlockedContent = null;

    this.clearPickAdditionalCharacterHint();
    this.setPartyPickDisabledCards([]);

    // Clear any pending preload timeout
    if (this.preloadTimeout) {
      clearTimeout(this.preloadTimeout);
    }

    if (this.loadingVideos) {
      this.loadingVideos.clear();
    }

    if (this.infiniteScrollObserver) {
      this.infiniteScrollObserver.disconnect();
      this.infiniteScrollObserver = null;
    }

    this.stopBannerPointerTracking();
    this.carouselWrapper?.removeEventListener('pointerdown', this.handleBannerPointerDown);
    this.carouselWrapper?.removeEventListener('touchmove', this.handleBannerTouchMove);
    this.element?.removeEventListener('click', this.handleBannerSlideClick);
    this.element?.removeEventListener('keydown', this.handleBannerSlideKeyDown);
    this.rankingsViewTabs?.removeEventListener('click', this.handleRankingsViewTabClick);
    this.recentChatsView?.destroy();
    this.recentChatsView = null;

    this.characterCards.forEach(card => card.destroy());
    this.characterCards.clear();

    [this.filterComponent, this.characterModal, this.faqComponent].forEach(component => {
      component?.destroy?.();
    });

    eventBus.off(EVENTS.RANKINGS.CHARACTERS_UPDATED, this.handleCharactersUpdated);
    eventBus.off(EVENTS.RANKINGS.ERROR_CHANGED, this.handleRankingsError);

    super.destroy();
  }
}
